import React, { useRef, useEffect, useState } from "react";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../actions";
import CommonContainerBox from "../custom/containers/CommonContainerBox";
import ERPSBaseRadioGroup from "../custom/inputs/ERPSBaseRadioGroup";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import CardList from "../custom/containers/cardList";
import ErrorMessage from "../custom/containers/ErrorMessage";
import { transactionSearchHeading } from "../../constants/TransactionSearchConstants.js";
import { getComponentAccess, setFinancialInstitutionUIData } from "../../utils/helper";
import { transactionTypes, RoleType }  from "../../constants/sharedConstants.js";
import { HStack, ResponsiveBox, Stack, Typography } from "../primitives";


const UpcomingRecallsRejects = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    history,
    location: { state },
  } = props;

  const user = useSelector((state) => state.loginUserReducer.user);
  const roleType = user.role.role_type;
  const currentFiNumber = useSelector((state) => state.fiReducer?.selectedFi || user?.fiNumber);

  const componentAccess = state?.componentAccess
          || getComponentAccess( { function_type_code: "URR", label_type_code: "TE" }, user );

  const urrResponse = useSelector(
    (state) => state.transactionEnquiryReducer
  );

  // Setting these two values from urrResponse if we're coming from the Detail Page (state?.fromDetailPag == true)
  const [searchType, setSearchType] = useState( state?.fromDetailPage && urrResponse?.upcomingRecallsRejectsDetails?.transactionType || transactionTypes[0].value);
  const [transactionData, setTransactionData] = useState( state?.fromDetailPage && urrResponse?.upcomingRecallsRejectsData || null);

  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [transactionTypeDisplay, setTransactionTypeDisplay] = useState([]);

  const [searchValues, setSearchValues] = useState({
    transactionType: searchType,
    offset: 0,
    limit: 100,
    currentFiNumber
  });
  
  const [fiMap, setFiMap] = useState(null);
  const fiReducer = useSelector( (state) => state.fiReducer );

  useEffect(() => {
    setFinancialInstitutionUIData(fiReducer?.financialInstitutionList, setFiMap, null, null, user?.languageCode);
  }, [fiReducer.financialInstitutionList, user?.languageCode]);

  useEffect(() => {
    if (!fiReducer?.financialInstitutionList?.length > 0)
        dispatch(actions.fiActions.getFinancialInstitutionList(t));

    setTransactionTypeDisplay( transactionTypes.map(tt => ({value: tt.value, name: t(tt.name, tt.name)}) ) );

    // if coming from the detail page, restore search values 
    if (state?.fromDetailPage && urrResponse?.upcomingRecallsRejectsDetails) { // if back from the Details page
      // set searchValues' attriutes from urrResponse.upcomingRecallsRejectsDetails
      const { transactionStatus, ...searchFields } = urrResponse.upcomingRecallsRejectsDetails;
      //setSearchValues({ ...searchFields });  -- This DOES NOT work: the next useEffect does not yet 'see' the properties set this way.
      Object.keys(searchFields).forEach(key => searchValues[key] = urrResponse.upcomingRecallsRejectsDetails[key]);

      setPageNumber(searchValues.offset / searchValues.limit);
      state.fromDetailPage = null;  // resetting the coming-from-detail-page flag
    } else {
      if (roleType === RoleType.BILLER_ADMIN.type || roleType === RoleType.BILLER_USER.type)
        searchValues.billerId = user.billerNonVolatileDto.biller_id;
    }
  }, []);

  const errorRef = useRef();
  useEffect(() => {
    if (errorRef.current)
      errorRef.current.focus();
  }, [errorMessage]);

  const searchTypeChanged = (searchTypeValue) => {
    if (loading) {
      // display msg
      setLoadingMsg("The page is loading, please wait");
    } else {
      // resetting back to the first page
      searchValues.offset = 0;
      setSearchType(searchTypeValue);
      setPageNumber(0);
    }
  };

  useEffect(() => {
    if (typeof actions.transactionEnquiryActions === 'undefined') {  // IS THERE A NEED TO CHECK ??
      console.log(ts() + " useEffect:  actions.transactionEnquiryActions is undefined");
    } else {
      searchValues.transactionType = searchType;
      loadResults("from useEffect[searchType]");
    }
  }, [searchType]);

  // Handling Response
  useEffect(() => {     //  (*.., [urrResponse])  -- called ONLY after a call to the service.  Actually, NOT SO!
    if (urrResponse) {
      const responseData = urrResponse.upcomingRecallsRejectsData?.data;
      if (responseData && responseData?.length)
        responseData.forEach((r, idx) => r.seqNo = idx + 1 + searchValues.offset);

      setTransactionData(responseData);        
    }
  }, [urrResponse]);

  const paginateURR = (values) => {  // 'values' has fields startOffset and endOffset (for 1st Page: 0,25;  2nd Page: 25,50;  3rd: 50,75...)
    searchValues.offset = values.startOffset;
    if (searchValues.limit !== (values.endOffset - values.startOffset))
      searchValues.limit = values.endOffset - values.startOffset;

    loadResults();
  };

  let loadResults = (tempText = "") => {
    const actualCriteria = { ...searchValues, transactionStatus: (searchType === transactionTypes[1].value) ? "Q" : "N" };
   
    if (errorMessage)
      setErrorMessage(null);

    if (loading) {
      setLoading(false);
      setLoadingMsg(null);
    }
    dispatch(
      actions.transactionEnquiryActions.searchUpcomingRejectsRecalls(
        actualCriteria,
        { ...props, history, setLoading, setErrorMessage }
      )
    );
  };

  function isShowFI() {
    // FI column is shown for Biller Admin/User;  but for FI Admin/User only for Rejects (i.e. transaction type is "RJ")
    return roleType === RoleType.BILLER_ADMIN.type || roleType === RoleType.BILLER_USER.type || searchType === "RJ";
  }

  return (
    <div role="main">
      <ResponsiveBox sx={{ ml: 3 }}>
        <ErrorMessage
          error={errorMessage}
          errorRef={errorRef}
          mt={2}
        />

        <ResponsiveBox className="manageUsers__page__title">
          {/* For Title update */}
          <CommonContainerBox title={t("erps.upcomingrecallsrejects.page.title")} />
          <Typography variant="h1">{t("erps.upcomingrecallsrejects.page.title")}</Typography>
        </ResponsiveBox>

        <HStack sx={{ mt: 2, spacing: 2 }}>
          <ResponsiveBox>
            <ERPSBaseRadioGroup
              name="searchBy"
              marginTop={"true"}
              //InputLabelProps={{ shrink: true }}
              value={searchType}
              onChange={(e) => searchTypeChanged(e.target.value)}
              items={transactionTypeDisplay}
              label=""   // seems like it's a mandatory prop
              //className={"transactionEnquiry__radioButton_direction"}
              //actionType={"hasURRSearchAccess"}   // DO I DEFINE A PERMISSION FOR THIS RADIO BTN ??
            />
          </ResponsiveBox>
        </HStack>

        {transactionData !== null && transactionData !== undefined && transactionData.length > 0 ? (
          <>
            <ResponsiveBox className="manageUsers_table_visible">
              <ResponsiveDataGrid
                data={{
                  rows: transactionData,
                  columns: transactionSearchHeading("URR", isShowFI() ? fiMap : null, "/upcoming-recalls-rejects/recall-reject-details"),
                  count: urrResponse?.upcomingRecallsRejectsData?.count || 0,
                }}
                ///initialLoading={initialLoading}
                sumOfColumnWidth="true"
                onAnyPaginationAction={paginateURR}
                pageNumber={pageNumber}
                setPage={setPageNumber}
                rowPerPage={searchValues.limit}
                tableLabelText={"_Page Title_"}
                //tableLabelText={t("erps.transactionEnquiry.page.title")}
                hideFooterPagination={false}
                pageName={"transaction-enquiry-urr"}
              />
            </ResponsiveBox>
            <ResponsiveBox className="manageUsers_card_visible">
              <CardList
                data={{
                  rows: transactionData,
                  columns: transactionSearchHeading("URR", isShowFI() ? fiMap : null, "/upcoming-recalls-rejects/recall-reject-details"),
                  count: urrResponse?.upcomingRecallsRejectsData?.count || 0,
                }}
                //onAnyPaginationAction={paginateTransactionEnquiry}
                hideFooterPagination={false}
                pageName={"upcoming-recalls-rejects"}
                pageNumber={pageNumber}
                setPage={setPageNumber}
                rowPerPage={searchValues.limit}
              />
            </ResponsiveBox>
          </>
        )
        : (
            <ResponsiveBox mt={1}>
                <Typography variant="body1">
                    { (transactionData?.length === 0)
                        ? (`0 ${t("erps.transactionEnquiry.page.resultfound")}`)
                        : (t("erps.availabledocuments.searchuser.notstarted", "Search not started"))
                    }
                </Typography>
            </ResponsiveBox>
          )
        }
      </ResponsiveBox>
    </div>
  );
};

export default withSidebar(UpcomingRecallsRejects);

function ts() {  return new Date().toISOString().substring(11, 23);  } // 2020-05-12T23:50:21.817Z
